import { Medal, UserCheck, Flame, Scale, ClipboardCheck, Home, Building2, Warehouse, GraduationCap } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/ui-kit/Reveal";
import QuoteButton from "@/components/ui-kit/QuoteButton";

export const SECURITY_INTRO = "When it comes to the safety of your premises, assets, and people, standard security is never enough. S4s Enterprises Pvt. Ltd. brings armed-forces discipline, uncompromising vigilance, and structured operational leadership directly to private facility protection.";

const REASONS = [
  { icon: Medal, title: "Military-Standard Leadership & SOPs", text: "Every post order, patrol schedule, and access control procedure is designed under veterans' guidance. We don’t just deploy guards; we execute structured defense protocols tailored to your site's vulnerabilities." },
  { icon: UserCheck, title: "Thoroughly Vetted & Rigorously Trained Personnel", text: "From comprehensive background verifications to rigorous drill training, our security officers are instructed in visitor management, gatehouse technology, perimeter surveillance, and emergency response." },
  { icon: Flame, title: "Crisis Management & Fire Safety Readiness", text: "Security must act as the primary line of defense. Our personnel undergo hands-on fire extinguisher operations, evacuation drills, and conflict de-escalation protocols so incidents are contained before they escalate." },
  { icon: Scale, title: "100% Statutory & Regulatory Compliance", text: "We adhere strictly to labor laws, statutory mandates (EPF, ESIC, Minimum Wages), and PSARA regulations. Partnering with us protects your organization from compliance risks and legal liabilities." },
  { icon: ClipboardCheck, title: "Active Field Supervision & Accountability", text: "Unlike passive agencies, our field officers conduct unscheduled day-and-night patrol audits, maintaining strict turnout standards and responsive communication channels with facility heads." },
];

const SECTORS = [
  { icon: Home, title: "Gated Residential Communities & Premium Societies", text: "Visitor logging, vehicle access control, boom-barrier monitoring, and peaceful residential safety." },
  { icon: Building2, title: "Corporate Tech Parks & Commercial Offices", text: "Turnstile management, badge validation, vendor handling, and sensitive asset safety." },
  { icon: Warehouse, title: "Warehouses & Industrial Manufacturing Plants", text: "Material in-out surveillance, inventory tracking, perimeter protection, and shift movement control." },
  { icon: GraduationCap, title: "Educational & Retail Complexes", text: "Crowd management, public safety protocols, and discreet incident handling." },
];

export const Leadership = ({ testId = "security-leadership", eyebrow = "Veteran-led", text = "Directed and mentored by an Ex-Serviceman Managing Director, our operations are built on the bedrock of military values: unyielding integrity, strategic crisis readiness, and zero room for error." }) => (
  <section data-testid={testId} className="bg-[var(--navy)] text-white relative overflow-hidden grain">
    <div aria-hidden className="absolute -right-10 top-1/2 w-[60%] h-[4px] bg-[var(--red)] -rotate-[14deg]" />
    <Reveal className="relative max-w-5xl mx-auto px-4 sm:px-8 py-20 sm:py-24">
      <Eyebrow light>{eyebrow}</Eyebrow>
      <p className="mt-6 font-display italic font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
        {text}
      </p>
    </Reveal>
  </section>
);

export const Reasons = ({ testId = "security-why-choose", eyebrow = "Why choose us", lead = "Why Enterprises", accent = "Choose S4s", sub, items = REASONS }) => (
  <section data-testid={testId} className="bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4 lg:sticky lg:top-28 self-start">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-5 font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9] text-[var(--ink)]">
          {lead} <span className="text-[var(--red)]">{accent}</span>
        </h2>
        {sub && <p className="mt-6 text-slate-600 leading-relaxed text-base">{sub}</p>}
      </Reveal>
      <div className="lg:col-span-8 divide-y divide-slate-200 border-t-2 border-[var(--ink)]">
        {items.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.05}>
            <article data-testid={`${testId}-reason-${i}`} className="group grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-8 py-8">
              <div className="w-14 h-14 grid place-items-center bg-[var(--red)] text-white chamfer-sm transition-transform duration-500 group-hover:-rotate-6">
                <r.icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display italic font-extrabold uppercase text-2xl sm:text-3xl leading-none text-[var(--ink)]">{r.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm sm:text-base">{r.text}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sectors = () => (
  <section data-testid="security-sectors" className="bg-[var(--paper)] py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <Reveal>
        <Eyebrow>Sectors</Eyebrow>
        <h2 className="mt-5 font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9] text-[var(--ink)]">Sectors We <span className="text-[var(--navy)]">Protect</span></h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 gap-6">
        {SECTORS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08} className="h-full">
            <article data-testid={`security-sector-${i}`} className="group h-full bg-white p-8 border-b-[3px] border-transparent hover:border-[var(--red)] transition-colors duration-500 shadow-[0_20px_50px_-30px_rgba(11,15,38,0.35)]">
              <s.icon className="w-8 h-8 text-[var(--navy)] transition-transform duration-500 group-hover:-translate-y-1" />
              <h3 className="mt-6 font-display italic font-extrabold uppercase text-2xl sm:text-3xl leading-none text-[var(--ink)]">{s.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed text-sm sm:text-base">{s.text}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Statement = ({ testId, text }) => (
  <section data-testid={testId} className="bg-[var(--paper)] py-20 sm:py-24">
    <Reveal className="max-w-5xl mx-auto px-4 sm:px-8 border-l-[6px] border-[var(--red)] pl-6 sm:pl-10">
      <p className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug text-[var(--ink)]">{text}</p>
    </Reveal>
  </section>
);

export const Closing = ({ testId = "security-closing-cta", lead = "Secure Your Facility with", accent = "Proven Leadership", text = "Partner with a security agency that values discipline as much as you value peace of mind." }) => (
  <section data-testid={testId} className="bg-[var(--ink)] text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-8">
        <h2 className="font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9]">{lead} <span className="text-[#ff4d53]">{accent}</span></h2>
        <p className="mt-5 text-slate-300 max-w-xl text-base sm:text-lg">{text}</p>
      </Reveal>
      <Reveal className="lg:col-span-4 lg:justify-self-end" delay={0.1}>
        <QuoteButton testId={`${testId}-quote-button`} />
      </Reveal>
    </div>
  </section>
);

export default function SecurityServiceContent() {
  return (
    <>
      <Leadership />
      <Reasons />
      <Sectors />
      <Closing />
    </>
  );
}
